import React, { useEffect, useMemo, useState } from "react";
import { useApi } from "../hooks/useApi";
import GrcControlWorkflow, { matchesWorkflowFilter, ownerOf, riskStatusLabel } from "./GrcControlWorkflow";
import { isOpenRisk } from "../hooks/useGrcRiskWorkflow";
import { isRemediationOverdue } from "../utils/riskWorkflow";
import { FINDING_STATE_LABEL } from "./AuditFindings";

// Security Insights → Issues (GRC flow step 6): everything open against the frameworks in one list.
// Open issues are the open GRC and Defender XDR risks from the tab's one risk read, each with its
// control and the framework requirements that control maps to. Approved policy exceptions are
// accepted deviations, not defects: they are listed in their own section and never counted as open
// issues (plan rule R4). Open audit findings are open issues too, listed after the risks: the server
// returns only the findings of audits this person is on (or every audit, for a manager), with the
// requirement, action owner and due date, and the full finding stays in the Audits tab. With NERC patch
// management on, the open patch risks (one per applicable patch still needing action, CIP-007 R2.3) are
// open issues too: they belong to no control, so the shared risk read leaves them out and this tab reads
// them itself; their work is recorded in GRC → Assets → NERC sites → Patch management.

// The control a risk belongs to: its SecurityControl for a GRC risk, else the row that links it.
export const controlOfRisk = (risk, controls) =>
    (risk.riskType === "GRC" && risk.securityControlId > 0
        ? controls.find(c => c.id === risk.securityControlId)
        : null)
    || controls.find(c => c.grcRiskId && String(c.grcRiskId) === String(risk.id))
    || null;

// The control's framework references, one entry per framework, in mapping order.
export const requirementsOfControl = (control) => {
    const byFramework = new Map();
    for (const m of control?.frameworkMappings || []) {
        if (!byFramework.has(m.framework)) byFramework.set(m.framework, []);
        const refs = byFramework.get(m.framework);
        if (!refs.includes(m.ref)) refs.push(m.ref);
    }
    return [...byFramework.entries()].map(([framework, refs]) => ({ framework, refs }));
};

// The open issues: open risks only, each with its control and requirements.
export const openIssues = (risks, controls) =>
    (risks || []).filter(isOpenRisk).map(risk => {
        const control = controlOfRisk(risk, controls || []);
        return { risk, control, requirements: requirementsOfControl(control) };
    });

export const PATCH_RISKS_URL = "/unifiedrisks/details?riskTypes=GRC&riskSources=NercPatch";

// Open NERC patch risks as issues: no control, the requirement they carry (CIP-007 R2.3).
export const patchIssues = (risks) =>
    (risks || []).filter(r => r.riskSource === "NercPatch" && isOpenRisk(r)).map(risk => ({
        risk, control: null, patch: true,
        requirements: [{ framework: "NERC CIP", refs: [risk.frameworkReference || "CIP-007 R2.3"] }],
    }));

export const filterIssues = (issues, { owner = "all", framework = "all", userId = null, search = "" } = {}) => {
    const q = search.trim().toLowerCase();
    return issues.filter(i =>
        matchesWorkflowFilter([i.risk], owner, userId)
        && (framework === "all" || i.requirements.some(r => r.framework === framework))
        && (!q || `${i.risk.riskTitle || ""} ${i.control?.title || ""}`.toLowerCase().includes(q)));
};

// The same filters over open audit findings: owner = action owner, framework = the audit's framework.
export const filterFindings = (findings, { owner = "all", framework = "all", userId = null, search = "" } = {}) => {
    const q = search.trim().toLowerCase();
    const today = new Date().toISOString().slice(0, 10);
    return (findings || []).filter(f =>
        (owner === "all"
            || (owner === "mine" && userId != null && f.actionOwnerUserId === userId)
            || (owner === "unassigned" && !f.actionOwnerUserId)
            || (owner === "overdue" && f.actionDueDate && f.actionDueDate.slice(0, 10) < today && f.state !== "Validated"))
        && (framework === "all" || f.framework === framework)
        && (!q || `${f.reference} ${f.title}`.toLowerCase().includes(q)));
};

const formatDate = (iso) => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : null;
};

const IssueRow = ({ issue, open, onToggle, frameworkLabel, frameworksFiltered, onWorkflowChanged, workflowBusyReason, onOpenControl, onViewRequirements }) => {
    const { risk, control, requirements } = issue;
    const due = formatDate(risk.remediationTargetDate);
    const overdue = isRemediationOverdue(risk.remediationTargetDate, risk.remediationStatus);
    const owner = ownerOf(risk, control?.remediationOwner);
    return (
        <li data-testid={`grc-issue-${risk.id}`} className={open ? "bg-gray-50" : ""}>
            <button type="button" onClick={onToggle} aria-expanded={open}
                className="w-full text-left px-3 py-2 flex flex-wrap items-start gap-x-3 gap-y-1 hover:bg-gray-50">
                <span className="text-xs font-semibold text-gray-800 flex-1 min-w-[12rem]">
                    {risk.riskTitle || control?.title || "Untitled risk"}
                    {control && control.title !== risk.riskTitle && <span className="block font-normal text-gray-500">{control.title}</span>}
                </span>
                <span className="text-xs text-gray-600 w-40 shrink-0">
                    {requirements.length === 0
                        ? <span className="text-gray-400">{frameworksFiltered ? "Not in the frameworks shown" : "No framework mapping"}</span>
                        : requirements.map(r => `${frameworkLabel(r.framework)} ${r.refs.join(", ")}`).join(" · ")}
                </span>
                <span className={`text-xs w-28 shrink-0 ${owner ? "text-gray-700" : "text-amber-700"}`}>{owner || "Unassigned"}</span>
                <span className={`text-xs w-28 shrink-0 ${overdue ? "text-red-600 font-semibold" : "text-gray-600"}`}>
                    {due ? `${overdue ? "Overdue · " : ""}${due}` : "No target date"}
                </span>
                <span className="text-xs text-gray-600 w-24 shrink-0">{riskStatusLabel(risk.status)}</span>
            </button>
            {open && (
                <div className="px-3 pb-3" data-testid={`grc-issue-detail-${risk.id}`}>
                    <GrcControlWorkflow risks={[risk]} ownerName={control?.remediationOwner}
                        onChanged={onWorkflowChanged} disabledReason={workflowBusyReason}
                        evidenceControlId={control?.controlId || null}
                        className="bg-white border border-gray-200 rounded-lg p-3 mb-2" />
                    {issue.patch && (
                        <p className="text-xs text-gray-600 mb-2" data-testid={`grc-issue-patch-note-${risk.id}`}>
                            Record each asset&apos;s decision in GRC → Assets → NERC sites → Patch management. Closing this risk does not
                            complete the patch: while the patch still needs action a new risk replaces it.
                        </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {control && onOpenControl && (
                            <button type="button" onClick={() => onOpenControl(control)}
                                className="text-xs px-2 py-0.5 border border-indigo-200 rounded text-indigo-700 hover:bg-indigo-50">
                                Open in Controls
                            </button>
                        )}
                        {control && onViewRequirements && requirements.map(r => (
                            <button key={r.framework} type="button" onClick={() => onViewRequirements(r.framework, control)}
                                className="text-xs px-2 py-0.5 border border-gray-200 rounded text-gray-700 hover:bg-gray-100">
                                {frameworkLabel(r.framework)} requirements →
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </li>
    );
};

const GrcIssuesTab = ({
    controls = [], workflow = null, currentUserId = null, onWorkflowChanged, workflowBusyReason = null,
    onOpenControl, onViewRequirements, frameworkLabel = (fw) => fw,
    // controls carry only the mappings of the frameworks shown (GrcFrameworkView) when this is true
    frameworksFiltered = false,
    // NERC patch management is on: list the open patch risks too
    includePatchRisks = false,
}) => {
    const apiCall = useApi();
    const [owner, setOwner] = useState("all");
    const [framework, setFramework] = useState("all");
    const [search, setSearch] = useState("");
    const [openId, setOpenId] = useState(null);
    const [exceptions, setExceptions] = useState({ loading: true, failed: false, items: [] });

    // Approved exceptions across the organization's policies, read once.
    useEffect(() => {
        apiCall("GET", "/security-insights/policy-docs/exceptions")
            .then(list => setExceptions({
                loading: false, failed: false,
                items: (Array.isArray(list) ? list : []).filter(x => x.exception?.status === "Active"),
            }))
            .catch(() => setExceptions({ loading: false, failed: true, items: [] }));
    }, [apiCall]);

    // Open audit findings this person may see (none for someone on no audit).
    const [findings, setFindings] = useState({ loading: true, failed: false, items: [] });
    useEffect(() => {
        apiCall("GET", "/security-insights/audits/findings")
            .then(list => setFindings({ loading: false, failed: false, items: Array.isArray(list) ? list : [] }))
            .catch(() => setFindings({ loading: false, failed: true, items: [] }));
    }, [apiCall]);

    // The open NERC patch risks (flag on only), reread after a workflow action on one of them.
    const [patchRisks, setPatchRisks] = useState([]);
    const [patchReload, setPatchReload] = useState(0);
    useEffect(() => {
        if (!includePatchRisks) { setPatchRisks([]); return; }
        apiCall("GET", PATCH_RISKS_URL)
            .then(list => setPatchRisks(Array.isArray(list) ? list : []))
            .catch(() => setPatchRisks([]));
    }, [apiCall, includePatchRisks, patchReload]);
    const onPatchWorkflowChanged = (...args) => { setPatchReload(n => n + 1); onWorkflowChanged?.(...args); };

    const issues = useMemo(() => [...openIssues(workflow?.risks, controls), ...patchIssues(patchRisks)],
        [workflow?.risks, controls, patchRisks]);
    const shown = useMemo(() => filterIssues(issues, { owner, framework, userId: currentUserId, search }),
        [issues, owner, framework, currentUserId, search]);
    const shownFindings = useMemo(() => filterFindings(findings.items, { owner, framework, userId: currentUserId, search }),
        [findings.items, owner, framework, currentUserId, search]);
    const frameworks = useMemo(() => {
        const seen = new Set();
        issues.forEach(i => i.requirements.forEach(r => seen.add(r.framework)));
        findings.items.forEach(f => seen.add(f.framework));
        return [...seen].sort((a, b) => frameworkLabel(a).localeCompare(frameworkLabel(b)));
    }, [issues, findings.items, frameworkLabel]);

    return (
        <div className="space-y-4" data-testid="grc-issues-tab">
            <div className="bg-white border border-gray-200 rounded-lg" data-testid="grc-open-issues">
                <div className="p-3 border-b border-gray-200 flex flex-wrap items-center gap-2">
                    <div className="mr-auto">
                        <div className="text-sm font-semibold text-gray-800">Open issues ({issues.length + findings.items.length})</div>
                        <div className="text-xs text-gray-500">
                            Open risks raised by failing controls, with the requirements they affect, {includePatchRisks ? "open NERC patch decisions, " : ""}and open audit findings.
                        </div>
                    </div>
                    <select value={owner} onChange={e => setOwner(e.target.value)} aria-label="Issue owner filter"
                        data-testid="grc-issue-owner-filter" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                        <option value="all">All owners</option>
                        <option value="mine">Assigned to me</option>
                        <option value="unassigned">Unassigned</option>
                        <option value="overdue">Overdue</option>
                    </select>
                    <select value={framework} onChange={e => setFramework(e.target.value)} aria-label="Issue framework filter"
                        data-testid="grc-issue-framework-filter" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                        <option value="all">All frameworks</option>
                        {frameworks.map(f => <option key={f} value={f}>{frameworkLabel(f)}</option>)}
                    </select>
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search issues" aria-label="Search issues"
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-48" />
                </div>
                {!workflow?.loaded ? (
                    <div className="p-6 text-center text-xs text-gray-400">
                        {workflow?.failed ? "Could not load the open issues. Try again." : "Loading open issues…"}
                    </div>
                ) : shown.length === 0 ? (shownFindings.length === 0 && (
                    <div className="p-6 text-center text-xs text-gray-400">
                        {issues.length === 0 && findings.items.length === 0 ? "No open issues." : "No issue matches these filters."}
                    </div>
                )) : (
                    <ul className="divide-y divide-gray-100">
                        {shown.map(issue => (
                            <IssueRow key={issue.risk.id} issue={issue} open={openId === issue.risk.id}
                                onToggle={() => setOpenId(openId === issue.risk.id ? null : issue.risk.id)}
                                frameworkLabel={frameworkLabel} frameworksFiltered={frameworksFiltered}
                                onWorkflowChanged={issue.patch ? onPatchWorkflowChanged : onWorkflowChanged}
                                workflowBusyReason={workflowBusyReason} onOpenControl={onOpenControl}
                                onViewRequirements={onViewRequirements} />
                        ))}
                    </ul>
                )}
                {findings.failed ? (
                    <div className="p-3 border-t border-gray-200 text-center text-xs text-red-600" role="alert">Could not load the audit findings.</div>
                ) : shownFindings.length > 0 && (
                    <div className="border-t border-gray-200" data-testid="grc-open-findings">
                        <div className="px-3 pt-2 text-xs font-semibold text-gray-600">Audit findings ({shownFindings.length}) — details are in the Audits tab</div>
                        <ul className="divide-y divide-gray-100">
                            {shownFindings.map(f => {
                                const overdue = f.actionDueDate && f.actionDueDate.slice(0, 10) < new Date().toISOString().slice(0, 10) && f.state !== "Validated";
                                return (
                                    <li key={f.id} data-testid={`grc-finding-${f.reference}`} className="px-3 py-2 flex flex-wrap items-start gap-x-3 gap-y-1 text-xs">
                                        <span className="font-semibold text-gray-800 flex-1 min-w-[12rem]">
                                            {f.reference} · {f.title}
                                            <span className="block font-normal text-gray-500">{f.engagementName}</span>
                                        </span>
                                        <span className="text-gray-600 w-40 shrink-0">{frameworkLabel(f.framework)} {f.requirementDisplayId}</span>
                                        <span className={`w-28 shrink-0 ${f.actionOwnerName ? "text-gray-700" : "text-amber-700"}`}>{f.actionOwnerName || "No action owner"}</span>
                                        <span className={`w-28 shrink-0 ${overdue ? "text-red-600 font-semibold" : "text-gray-600"}`}>
                                            {f.actionDueDate ? `${overdue ? "Overdue · " : ""}${formatDate(f.actionDueDate)}` : "No due date"}
                                        </span>
                                        <span className="text-gray-600 w-24 shrink-0">{FINDING_STATE_LABEL[f.state] || f.state}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg" data-testid="grc-accepted-exceptions">
                <div className="p-3 border-b border-gray-200">
                    <div className="text-sm font-semibold text-gray-800">Accepted exceptions ({exceptions.items.length})</div>
                    <div className="text-xs text-gray-500">Approved deviations from a policy. They are not open issues and are not counted above.</div>
                </div>
                {exceptions.loading ? (
                    <div className="p-4 text-center text-xs text-gray-400">Loading exceptions…</div>
                ) : exceptions.failed ? (
                    <div className="p-4 text-center text-xs text-red-600" role="alert">Could not load the exceptions.</div>
                ) : exceptions.items.length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-400">No approved exceptions.</div>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {exceptions.items.map(x => (
                            <li key={x.exception.id} className="px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
                                data-testid={`grc-exception-${x.exception.id}`}>
                                <span className="font-semibold text-gray-800 flex-1 min-w-[12rem]">{x.exception.title}</span>
                                <span className="text-gray-600">Policy: {x.policyTitle}</span>
                                {x.exception.affectedObject && <span className="text-gray-500">{x.exception.affectedObject}</span>}
                                <span className="text-gray-600">{formatDate(x.exception.expiryDate) ? `Expires ${formatDate(x.exception.expiryDate)}` : "No expiry"}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default GrcIssuesTab;
